import { Alert } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField/TextField";
import * as Storage from "firebase/storage";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  usePaymentEmailStr,
  useWarningEmailStr,
} from "@/v2/hooks/constant-hooks";
import { getPricingPlans } from "../../../apis/rental/city-apis";
import { payForRental } from "../../../apis/rental/legacy-fine-apis";
import { sendWarningToRental } from "../../../apis/rental/rental-apis";
import { getUser } from "../../../apis/user/user-apis";
import type {
  Rental,
  RentalPaymentResult,
  RentalWarningResult,
} from "../../../domain/rental";
import type { User } from "../../../domain/user";
import { constructImageDirectory } from "../../../utils/utils";
import { NoImage } from "../../no-image/NoImage";
import { RentalsItemPaymentResult } from "./RentalsItemPaymentResult";
import { RentalsItemWarningResult } from "./RentalsItemWarningResult";

interface Props {
  rental: Rental;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const RentalsItemPayment = ({ rental, onClose }: Props) => {
  const allCities = useSelector((s: any) => s.cities.citiesList);
  const adminDetails = useSelector((s: any) => s.auth.adminDetails);
  const [imageUrl, setImageUrl] = useState("");
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [emailStr, setEmailStr] = useState(usePaymentEmailStr());
  const [warningEmailStr, setWarningEmailStr] = useState(useWarningEmailStr());
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [result, setResult] = useState<
    RentalPaymentResult | RentalWarningResult | null
  >(null);
  const [tabValue, setTabValue] = useState(0);
  const couldSendEmail = user?.email;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setResult(null);
  };

  const onSubmit = () => {
    setLoading(true);
    payForRental(rental.id, { amount: amount * 100, description, emailStr })
      .then((result) => {
        setResult(result);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onSubmitWarning = () => {
    setLoading(true);
    sendWarningToRental(rental.id, { emailStr: warningEmailStr })
      .then((result) => {
        setResult(result);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const cityCode = allCities.find((c: any) => c.id === rental.cityId)?.code;

    if (rental?.cityId && cityCode) {
      getPricingPlans(rental.cityId).then((plans) => {
        const directory = constructImageDirectory({
          cityCode,
          pricingPlanType: plans.find((p) => p.id === rental.planId)?.type,
          id: rental.id,
        });

        const storage = Storage.getStorage();
        const storageRef = Storage.ref(storage, directory);

        Storage.list(storageRef, { maxResults: 1 })
          .then(({ items: [item] }) => {
            return item ? Storage.getDownloadURL(item) : "";
          })
          .then((url) => {
            setImageUrl(url);
          });
      });
    }
  }, [rental]);

  useEffect(() => {
    if (rental?.userId) {
      getUser(rental.userId).then((result) => {
        setUser(result);
      });
    } else {
      setUser(null);
    }
  }, [rental]);

  return (
    <Modal className={"modalContainer"} open={true} onClose={() => onClose()}>
      <Card
        variant="outlined"
        sx={{
          width: { xs: "95vw", sm: "90vw", md: "80vw" },
          height: { xs: "95vh", md: "80vh" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {imageUrl ? (
          <CardMedia
            component="img"
            src={imageUrl}
            sx={{
              width: { xs: "100%", md: "40%" },
              height: { xs: "200px", md: "auto" },
              objectFit: "cover",
            }}
          />
        ) : (
          <CardMedia
            sx={{
              width: { xs: "100%", md: "40%" },
              height: { xs: "200px", md: "auto" },
            }}
          >
            <NoImage />
          </CardMedia>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              overflow: "hidden",
              p: { xs: 2, md: 3 },
              height: "100%",
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h4" style={{ margin: 0 }}>
                {tabValue === 0
                  ? `Make a payment for ${rental.id}`
                  : `Send warning for ${rental.id}`}
              </Typography>
              {!!user && (
                <Typography variant="h6" style={{ margin: 0 }}>
                  <Link
                    to={
                      adminDetails?.newBackendEnabled
                        ? `/dashboard/customer/${rental.userId}/${rental.cityId}`
                        : `/dashboard/user/${rental.userId}/${rental.cityId}`
                    }
                    target="_blank"
                    style={{ color: "blue" }}
                  >
                    {user.fullName || user.id}
                  </Link>{" "}
                  {tabValue === 0
                    ? "will be charged"
                    : "will receive a warning"}
                </Typography>
              )}
            </Stack>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="fine or warning tabs"
              >
                <Tab label="Give a fine" />
                <Tab label="Give a warning" />
              </Tabs>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                my: 2,
              }}
            >
              {!result && (
                <>
                  {tabValue === 0 && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <TextField
                          value={amount}
                          onChange={(e) => setAmount(Number(e.target.value))}
                          label="Amount"
                          type="number"
                          inputProps={{ min: 0 }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                        <TextField
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          label="Description"
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                          border: "2px solid #1976d2",
                          borderRadius: 2,
                          background: "#fff",
                          p: 0.5,
                        }}
                      >
                        <TextField
                          value={emailStr}
                          onChange={(e) => setEmailStr(e.target.value)}
                          label="Email (Optional)"
                          multiline={true}
                          fullWidth={true}
                          minRows={10}
                          variant="standard"
                          sx={{
                            flexGrow: 1,
                            minHeight: 0,
                            overflow: "auto",
                            resize: "none",
                            border: "none",
                            background: "transparent",
                          }}
                          InputProps={{ disableUnderline: true }}
                        />
                      </Box>
                    </Box>
                  )}
                  {tabValue === 1 && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        border: "2px solid #1976d2",
                        borderRadius: 2,
                        background: "#fff",
                        p: 0.5,
                      }}
                    >
                      <TextField
                        value={warningEmailStr}
                        onChange={(e) => setWarningEmailStr(e.target.value)}
                        multiline={true}
                        label="Email"
                        disabled={!couldSendEmail}
                        fullWidth={true}
                        minRows={10}
                        variant="standard"
                        sx={{
                          flexGrow: 1,
                          minHeight: 0,
                          overflow: "auto",
                          resize: "none",
                          border: "none",
                          background: "transparent",
                        }}
                        InputProps={{ disableUnderline: true }}
                      />
                    </Box>
                  )}
                </>
              )}
              {!!result && (
                <>
                  {tabValue === 0 && "chargeStatus" in result && (
                    <RentalsItemPaymentResult
                      result={result as RentalPaymentResult}
                      rental={rental}
                    />
                  )}
                  {tabValue === 1 &&
                    "emailStatus" in result &&
                    !("chargeStatus" in result) && (
                      <RentalsItemWarningResult
                        result={result as RentalWarningResult}
                        rental={rental}
                      />
                    )}
                </>
              )}
            </Box>
            {!result && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: 1,
                  borderColor: "divider",
                  pt: 2,
                  mt: 2,
                  background: "white",
                  flexShrink: 0,
                }}
              >
                {tabValue === 0 ? (
                  couldSendEmail ? (
                    <Alert
                      severity="info"
                      sx={{
                        m: 0,
                        p: 0,
                        background: "none",
                        alignItems: "center",
                      }}
                    >
                      The email will be sent to <u>{user?.email}</u>
                    </Alert>
                  ) : (
                    <Alert
                      severity="warning"
                      sx={{
                        m: 0,
                        p: 0,
                        background: "none",
                        alignItems: "center",
                      }}
                    >
                      The email couldn't be sent because the user doesn't have
                      an email address
                    </Alert>
                  )
                ) : couldSendEmail ? (
                  <Alert
                    severity="info"
                    sx={{
                      m: 0,
                      p: 0,
                      background: "none",
                      alignItems: "center",
                    }}
                  >
                    The warning email will be sent to <u>{user?.email}</u>
                  </Alert>
                ) : (
                  <Alert
                    severity="warning"
                    sx={{
                      m: 0,
                      p: 0,
                      background: "none",
                      alignItems: "center",
                    }}
                  >
                    The warning email couldn't be sent because the user doesn't
                    have an email address
                  </Alert>
                )}
                {tabValue === 0 ? (
                  <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={loading || !amount || !description}
                  >
                    {loading ? <CircularProgress size={20} /> : "Capture"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={onSubmitWarning}
                    disabled={
                      loading || !warningEmailStr.trim() || !couldSendEmail
                    }
                  >
                    {loading ? <CircularProgress size={20} /> : "Send Warning"}
                  </Button>
                )}
              </Box>
            )}
            {!!result && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  mt: "auto",
                  flexShrink: 0,
                }}
              >
                <Button variant="contained" onClick={() => onClose()}>
                  Close
                </Button>
              </Box>
            )}
          </CardContent>
        </Box>
      </Card>
    </Modal>
  );
};
